import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ImageItem, ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItem, VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {ImageSlideItem, ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {AddTranscriptOpts, ImageData, PMWShapeData, TempImageData, VideoData} from '@Libraries/add-media-library';
import {isTempImageData, ElementDataType, getGettyAssetClassification, getImageDataForPmwStockImageFamilyElement} from '@Libraries/add-media-library';
import {prepImageItemObjectFromImageData, prepVideoItemObjectFromVideoData} from '@PosterWhiteboard/items/item/element-data-to-item.library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {loadImageAsync} from '@Utils/image.util';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {getFilenameForVideo, getRepoVideoURL} from '@Libraries/user-video-library';
import {loadVideo} from '@Utils/video.util';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {VectorItem} from '@PosterWhiteboard/items/vector-item/vector-item.class';
import type {LineItem} from '@PosterWhiteboard/items/line-item/line-item';
import type {RectangleItem} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {BaseItemObject, SlideItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {CropperPanelApplyResponse} from '@Panels/cropper-panel';
import type {VideoCropperPanelApplyResponse} from '@Panels/video-cropper-panel/video-cropper-panel.types';
import {getUniqueString} from '@Utils/string.util';
import type {ItemData, NewImageItemData, NewTempImageItemData, NewTranscriptData} from '@PosterWhiteboard/page/add-item.class';
import {getImageScreenURL} from '@Libraries/image-item.library';
import type {MaskingShape, MaskingShapeObject} from '@PosterWhiteboard/classes/masking/masking-shape.class';
import type {MaskingText, MaskingTextObject} from '@PosterWhiteboard/classes/masking/masking-text.class';
import {DEFAULT_SLIDE_DURATION} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import type {DeepPartial} from '@/global';
import {PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import {convertFabricShadowObjectToShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';

export class ReplaceItem {
  public page: Page;

  private static readonly ASPECT_RATIO_TOLERANCE = 0.01;
  private static readonly IMAGE_ASPECT_RATIO_RELATIVE_TOLERANCE = 0.15;

  public constructor(page: Page) {
    this.page = page;
  }

  public async replaceTranscript(uidOfTranscriptToReplace: string, uidForTranscriptToAdd: string, itemToReplaceWith: NewTranscriptData, opts: AddTranscriptOpts): Promise<void> {
    const transcriptItemToReplace = this.page.items.getItem(uidOfTranscriptToReplace);
    if (!transcriptItemToReplace) {
      throw new Error(`Could not find existing transcript item with uid ${uidOfTranscriptToReplace}`);
    }
    if (!transcriptItemToReplace.isTranscript()) {
      throw new Error('Expected a transcript item, but got something else');
    }

    itemToReplaceWith.selectedSubtitleTemplateId = transcriptItemToReplace.getAnySubtitle().selectedTemplateId;
    itemToReplaceWith.styles = {
      sentenceTextStyles: transcriptItemToReplace.getAnySubtitle().sentenceTextStyles,
      activeWordTextStyles: transcriptItemToReplace.getAnySubtitle().activeWordTextStyles,
      shadow: convertFabricShadowObjectToShadowOrGlowOptionsObject(transcriptItemToReplace.getAnySubtitle().toObject().shadow, transcriptItemToReplace.getScaleForShadow()),
      backgroundFill: transcriptItemToReplace.getAnySubtitle().backgroundFill,
      backgroundBorderRadius: transcriptItemToReplace.getAnySubtitle().backgroundBorderRadius,
      animationStyle: transcriptItemToReplace.getAnySubtitle().animationStyle,
    };

    this.page.poster.deleteItemById(uidOfTranscriptToReplace, false);

    await this.page.items.addItems.addTranscriptItem(itemToReplaceWith, {
      optionsToAdd: {
        left: transcriptItemToReplace.fabricObject.left,
        top: transcriptItemToReplace.fabricObject.top,
        width: transcriptItemToReplace.fabricObject.width,
        height: transcriptItemToReplace.fabricObject.height,
        lockMovementX: transcriptItemToReplace.fabricObject.lockMovementX,
        angle: transcriptItemToReplace.fabricObject.angle,
        ...opts.optionsToAdd,
        uid: uidForTranscriptToAdd,
      },
      selectOnAdd: opts.selectOnAdd,
    });
  }

  public async replaceMedia(itemToReplaceWith: ItemData, activeItem: ImageItem | VideoItem | ImageSlideItem | VideoSlideItem, allowAspectRatioTolerance = false): Promise<void> {
    if (!this.page.items.getItem(activeItem.uid)) {
      return;
    }

    // Icon/sticker stock picks land as their own element types; the image flows below only speak ImageData.
    const itemData = getImageDataForPmwStockImageFamilyElement(itemToReplaceWith) ?? itemToReplaceWith;

    // Backgrounds swap via the dedicated bg path: it fits the replacement into the old bg's
    // exact timeline slot (the generic path deletes-then-re-adds and corrupts multi-bg timing)
    // and applies a silent page-aspect center crop instead of opening the replace-crop modal.
    if (activeItem.isBackground && itemData.type === ElementDataType.IMAGE) {
      await this.page.items.addItems.onAddImageBackground([itemData], 'replace', activeItem);
      return;
    }

    const {allowed, droppedTypes} = this.page.poster.filterByGettyLimits([itemData], getGettyAssetClassification, {excludeItemUids: new Set([activeItem.uid])});
    getPosterUi().notifyGettyLimitReached(droppedTypes);
    if (allowed.length === 0) {
      return;
    }

    const scaledWidth = activeItem.fabricObject.getScaledWidth();
    const scaledHeight = activeItem.fabricObject.getScaledHeight();

    if (itemData.type === ElementDataType.IMAGE) {
      return this.replaceWithImage(
        {
          scaledWidth,
          scaledHeight,
          maintainAspectRatioForScaledDimension: true,
          ...itemData,
        },
        activeItem,
        allowAspectRatioTolerance
      );
    }
    if (itemData.type === ElementDataType.VIDEO) {
      return this.replaceWithVideo(
        {
          scaledWidth,
          scaledHeight,
          maintainAspectRatioForScaledDimension: true,
          ...itemData,
        },
        activeItem
      );
    }

    this.#reportUnexpectedReplaceType(itemData.type);
  }

  public async replaceMediaSlide(itemToReplaceWith: ItemData, activeSlide: ImageSlideItem | VideoSlideItem, activeItem: SlideshowItem): Promise<void> {
    if (!this.page.items.getItem(activeItem.uid) || !activeItem.slides.slidesHashMap[activeSlide.uid]) {
      return;
    }

    const itemData = getImageDataForPmwStockImageFamilyElement(itemToReplaceWith) ?? itemToReplaceWith;

    const {allowed, droppedTypes} = this.page.poster.filterByGettyLimits([itemData], getGettyAssetClassification, {excludeItemUids: new Set([activeSlide.uid])});
    getPosterUi().notifyGettyLimitReached(droppedTypes);
    if (allowed.length === 0) {
      return;
    }

    if (itemData.type === ElementDataType.IMAGE) {
      return this.replaceSlideWithImageSlide(itemData, activeSlide, activeItem);
    }
    if (itemData.type === ElementDataType.VIDEO) {
      return this.replaceSlideWithVideoSlide(itemData, activeSlide, activeItem);
    }

    this.#reportUnexpectedReplaceType(itemData.type);
  }

  /**
   * Every replace entry point is invoked fire-and-forget, so throwing here would surface only as an
   * unhandled rejection the user never sees. Report the type that slipped through the normalizer and
   * show the same failure modal the other replace paths use.
   */
  #reportUnexpectedReplaceType(unexpectedType: ElementDataType): void {
    console.error(`unexpected element type received in replace: ${unexpectedType}`);
    this.errorModalWhileReplacingItem();
  }

  public async replaceSlideWithVideoSlide(item: VideoData, activeSlide: ImageSlideItem | VideoSlideItem, activeItem: SlideshowItem): Promise<void> {
    const itemToAdd: Partial<VideoSlideItem> = {};
    const uid = getUniqueString();
    itemToAdd.hashedFilename = item.hashedFilename;
    itemToAdd.duration = item.duration;
    itemToAdd.endTime = item.duration;
    itemToAdd.frameRate = item.frameRate;
    itemToAdd.videoSource = item.source;
    // TODO: transparency
    itemToAdd.startTime = 0;
    itemToAdd.fileExtension = item.extension;
    itemToAdd.slideDuration = item.duration;

    itemToAdd.uid = uid;

    const {slidesOrder, slidesHashMap} = activeItem.slides.toObject();

    const visuals = {};
    if (activeSlide.gitype === ITEM_TYPE.VIDEOSLIDE) {
      this.copyVisuals(visuals, activeSlide);
    }

    for (let index = 0; index < slidesOrder.length; index += 1) {
      if (slidesOrder[index] === activeSlide.uid) {
        slidesOrder[index] = uid;
      }
    }

    slidesHashMap[uid] = {
      ...visuals,
      gitype: ITEM_TYPE.VIDEOSLIDE,
      videoSource: itemToAdd.fileExtension,
      fileExtension: itemToAdd.fileExtension,
      hashedFilename: itemToAdd.hashedFilename,
      duration: itemToAdd.duration,
      frameRate: itemToAdd.frameRate,
      uid: itemToAdd.uid,
      slideDuration: itemToAdd.slideDuration,
    } as SlideItemObject;

    delete slidesHashMap[activeSlide.uid];

    showLoading('replaceWithVideoSlide');
    activeItem
      .updateFromObject(
        {
          slides: {
            slidesOrder,
            slidesHashMap,
          },
        },
        {}
      )
      .then((): void => {})
      .catch((): void => {})
      .finally((): void => {
        hideLoading('replaceWithVideoSlide');
      });
  }

  public async replaceSlideWithImageSlide(item: ImageData | TempImageData, activeSlide: ImageSlideItem | VideoSlideItem, activeItem: SlideshowItem): Promise<void> {
    const itemToAdd: Partial<ImageSlideItemObject> = {};
    const uid = 'uid' in item ? item.uid : getUniqueString();
    itemToAdd.imageSource = item.source;
    itemToAdd.uid = uid;

    const {slidesOrder, slidesHashMap} = activeItem.slides.toObject();

    const visuals = {};
    if (activeSlide.gitype === ITEM_TYPE.IMAGESLIDE) {
      this.copyVisuals(visuals, activeSlide);
    }

    for (let index = 0; index < slidesOrder.length; index += 1) {
      if (slidesOrder[index] === activeSlide.uid) {
        slidesOrder[index] = uid;
      }
    }
    slidesHashMap[uid] = {
      ...visuals,
      gitype: ITEM_TYPE.IMAGESLIDE,
      imageSource: itemToAdd.imageSource,
      uid,
      slideDuration: DEFAULT_SLIDE_DURATION,
    } as SlideItemObject;

    if (isTempImageData(item)) {
      slidesHashMap[uid] = {
        ...slidesHashMap[uid],
        uploadingImageData: {
          tempUploadingImageUID: uid,
          dataUrl: item.dataUrl,
          uploadStartTime: new Date().getTime(),
        },
      };
    } else {
      slidesHashMap[uid] = {
        ...slidesHashMap[uid],
        fileExtension: item.extension,
        hashedFilename: item.hashedFilename,
        isUpscaled: item.isUpscaled ?? false,
      };
    }

    delete slidesHashMap[activeSlide.uid];

    showLoading('replaceWithImageSlide');
    await activeItem
      .updateFromObject({
        slides: {
          slidesOrder,
          slidesHashMap,
        },
      })
      .then((): void => {})
      .catch((): void => {})
      .finally((): void => {
        hideLoading('replaceWithImageSlide');
      });
  }

  private copyVisuals(itemToAdd: DeepPartial<ImageItemObject | VideoItemObject>, activeItem: ImageItem | VideoItem): void {
    this.#copyCommonVisuals(itemToAdd, activeItem);
    itemToAdd.border = activeItem.border.toObject();
    itemToAdd.effects = activeItem.effects.toObject();
  }

  /** Opacity, drop shadow and the lock flags — the visual state every item type carries on BaseItemObject. */
  #copyCommonVisuals(itemToAdd: DeepPartial<BaseItemObject>, activeItem: Item): void {
    itemToAdd.lockMovementX = activeItem.fabricObject.lockMovementX;
    itemToAdd.lockMovementY = activeItem.fabricObject.lockMovementY;
    itemToAdd.lockRotation = activeItem.fabricObject.lockRotation;
    itemToAdd.lockScalingX = activeItem.fabricObject.lockScalingX;
    itemToAdd.lockScalingY = activeItem.fabricObject.lockScalingY;
    itemToAdd.shadow = activeItem.fabricObject.shadow?.toObject();
    itemToAdd.alpha = activeItem.fabricObject.opacity;
  }

  private copyMaskingItems(itemToAdd: DeepPartial<ImageItemObject>, itemToAddWidth: number, itemToAddHeight: number, activeItem: ImageItem): void {
    if (!activeItem.masking.maskingItem || activeItem.masking.maskingItem.isFreehand()) {
      return;
    }

    itemToAdd.masking = {
      ...activeItem.masking.toObject(),
      maskingItem: this.getCopiedMaskingItemObject(activeItem.masking.maskingItem, itemToAddWidth, itemToAddHeight),
    };
  }

  private getCopiedMaskingItemObject(maskingItem: MaskingShape | MaskingText, itemToAddWidth: number, itemToAddHeight: number): MaskingShapeObject | MaskingTextObject {
    const heightRatio = itemToAddHeight / maskingItem.imageHeight;
    const widthRatio = itemToAddWidth / maskingItem.imageWidth;
    const smallerRatio = widthRatio < heightRatio ? widthRatio : heightRatio;

    return {
      ...maskingItem.toObject(),
      imageWidth: itemToAddWidth,
      imageHeight: itemToAddHeight,
      top: maskingItem.top * smallerRatio,
      left: maskingItem.left * smallerRatio,
      scaleX: maskingItem.scaleX * smallerRatio,
      scaleY: maskingItem.scaleY * smallerRatio,
    };
  }

  public async replaceWithImage(
    item: NewImageItemData | NewTempImageItemData,
    activeItem: ImageItem | VideoItem | ImageSlideItem | VideoSlideItem,
    allowAspectRatioTolerance = false
  ): Promise<void> {
    const zIndex = activeItem.fabricObject.getZIndex();
    const itemToAdd: DeepPartial<ImageItemObject> = prepImageItemObjectFromImageData(item);
    itemToAdd.top = activeItem.fabricObject.getY();
    itemToAdd.left = activeItem.fabricObject.getX();
    itemToAdd.angle = activeItem.fabricObject.angle;
    if (activeItem.isBackground) {
      itemToAdd.isBackground = true;
      itemToAdd.onPageStartTime = activeItem.onPageStartTime;
      itemToAdd.onPageEndTime = activeItem.getPageEndTime();
    }

    if ('extension' in item && item.extension === 'svg') {
      this.page.poster.deleteItemById(activeItem.uid, false);
      this.page.activeSelection.clearSelection();
      void this.page.items.addItems.addImageItem(item, itemToAdd, {
        zIndex,
      });
      return;
    }

    let replacingAspectRatio = 0;
    if (activeItem.fabricObject.scaleX) {
      replacingAspectRatio = (activeItem.fabricObject.width * activeItem.fabricObject.scaleX) / (activeItem.fabricObject.height * activeItem.fabricObject.scaleY);
    } else {
      replacingAspectRatio = activeItem.fabricObject.width / activeItem.fabricObject.height;
    }

    if (activeItem.isImage()) {
      this.copyVisuals(itemToAdd, activeItem);
    }

    if (item) showLoading('LoadingImage');
    let imageSource;
    if ('hashedFilename' in item && 'extension' in item) {
      imageSource = getImageScreenURL(item.hashedFilename, item.extension, item.source);
    } else if (item.dataUrl) {
      imageSource = item.dataUrl;
    }
    if (!imageSource) {
      console.error('no image source found');
      return;
    }
    await loadImageAsync(imageSource)
      .then(async (img) => {
        this.page.poster.deleteItemById(activeItem.uid, false);
        this.page.activeSelection.clearSelection();

        const uid = 'uid' in item && item.uid ? item.uid : getUniqueString();

        let cropOnAdd = false;
        let maskOnAdd = false;
        if (activeItem.isImage()) {
          if (this.doesImageReplacementNeedCrop(replacingAspectRatio, img.width / img.height, allowAspectRatioTolerance)) {
            cropOnAdd = true;
          }

          if (activeItem.masking.maskingItem && !activeItem.masking.maskingItem.isFreehand()) {
            this.copyMaskingItems(itemToAdd, img.width, img.height, activeItem);
            cropOnAdd = false;
            maskOnAdd = true;
          }
        }

        if (activeItem.isVideo()) {
          if (Math.abs(replacingAspectRatio - img.width / img.height) > ReplaceItem.ASPECT_RATIO_TOLERANCE) {
            cropOnAdd = true;
          }
        }

        if (cropOnAdd) {
          if (item) hideLoading('LoadingImage');
          getPosterUi().closeStockMediaPopup();
          const cropperPanelResponse = await this.openCropModalAfterReplace(item, replacingAspectRatio);
          if (item) showLoading('LoadingImage');
          if (cropperPanelResponse) {
            itemToAdd.cropData = {
              cropped: true,
              ...cropperPanelResponse.cropData,
            };
            itemToAdd.removeBackground = {
              isBackgroundRemoved: cropperPanelResponse.isBackgroundRemoved,
            };
          }
        }

        await this.page.items.addItems.addImageItem(
          item,
          {uid, ...itemToAdd},
          {
            zIndex,
          }
        );

        if (maskOnAdd) {
          await this.openMaskingModalAfterReplace(uid);
        }
      })
      .catch(() => {
        this.errorModalWhileReplacingItem();
      })
      .finally(() => {
        hideLoading('LoadingImage');
      });
  }

  doesImageReplacementNeedCrop(currentAspectRatio: number, newAspectRatio: number, allowAspectRatioTolerance: boolean): boolean {
    if (allowAspectRatioTolerance) {
      return Math.abs(currentAspectRatio - newAspectRatio) / currentAspectRatio > ReplaceItem.IMAGE_ASPECT_RATIO_RELATIVE_TOLERANCE;
    }
    return Math.abs(currentAspectRatio - newAspectRatio) > ReplaceItem.ASPECT_RATIO_TOLERANCE;
  }

  private async openMaskingModalAfterReplace(itemUID: string): Promise<void> {
    const addedItem = this.page.poster.getItemForId(itemUID) as ImageItem;
    await getPosterUi().onOpenMaskingModal(addedItem);
  }

  private async openCropModalAfterReplace(addedItem: NewImageItemData | NewTempImageItemData, aspectRatio: number): Promise<CropperPanelApplyResponse | undefined> {
    return new Promise((resolve) => {
      getPosterUi().openCropperModal({
        onApply: resolve,
        onClose: (): void => {
          resolve(undefined);
        },
        aspectRatio,
        dataUrl: 'dataUrl' in addedItem ? addedItem.dataUrl : undefined,
        imageHashedFilename: 'hashedFilename' in addedItem ? addedItem.hashedFilename : undefined,
        imageSource: addedItem.source,
        showCancelButton: true,
        showRemoveBackgroundOption: addedItem.source !== PMWStockImageSource.PMW_STOCK_IMAGE,
        cancelButtonCtaText: window.i18next.t('pmwjs_use_full_size'),
        applyButtonCtaText: window.i18next.t('pmwjs_crop_and_replace'),
      });
    });
  }

  private async openVideoCropModalAfterReplace(videoSrc: string, aspectRatio: number): Promise<VideoCropperPanelApplyResponse | undefined> {
    return new Promise((resolve) => {
      getPosterUi().openVideoCropperModal({
        onApply: resolve,
        onClose: (): void => {
          resolve(undefined);
        },
        videoSrc,
        aspectRatio,
        applyButtonCtaText: window.i18next.t('pmwjs_crop_and_replace'),
      });
    });
  }

  public async replaceWithVideo(item: VideoData, activeItem: ImageItem | VideoItem | ImageSlideItem | VideoSlideItem): Promise<void> {
    const zIndex = activeItem.fabricObject.getZIndex();
    const itemToAdd: Partial<VideoItemObject> = prepVideoItemObjectFromVideoData(item);
    itemToAdd.left = activeItem.fabricObject.getX();
    itemToAdd.top = activeItem.fabricObject.getY();
    itemToAdd.angle = activeItem.fabricObject.angle;
    if (activeItem.isBackground) {
      itemToAdd.isBackground = true;
      itemToAdd.onPageStartTime = activeItem.onPageStartTime;
      itemToAdd.onPageEndTime = activeItem.getPageEndTime();
    }

    let replacingAspectRatio = 0;
    if (activeItem.fabricObject.scaleX) {
      replacingAspectRatio = (activeItem.fabricObject.width * activeItem.fabricObject.scaleX) / (activeItem.fabricObject.height * activeItem.fabricObject.scaleY);
    } else {
      replacingAspectRatio = activeItem.fabricObject.width / activeItem.fabricObject.height;
    }

    showLoading('loadingVideo');
    const fileNameForVideo = getFilenameForVideo(item.hashedFilename, item.extension, item.hasTransparency);
    const videoSource = getRepoVideoURL(fileNameForVideo, this.page.poster.isHighRes);

    if (activeItem.gitype === ITEM_TYPE.VIDEO) {
      this.copyVisuals(itemToAdd, activeItem);
    }

    loadVideo(videoSource)
      .then(async (video) => {
        const newVideoAspectRatio = video.videoWidth / video.videoHeight;
        const cropOnAdd = Math.abs(replacingAspectRatio - newVideoAspectRatio) > ReplaceItem.ASPECT_RATIO_TOLERANCE;

        if (cropOnAdd) {
          hideLoading('loadingVideo');
          getPosterUi().closeStockMediaPopup();
          const videoCropperResponse = await this.openVideoCropModalAfterReplace(videoSource, replacingAspectRatio);
          showLoading('loadingVideo');

          if (videoCropperResponse) {
            itemToAdd.cropData = {
              cropped: true,
              ...videoCropperResponse.cropData,
            };
          }
        }

        itemToAdd.width = video.width;
        itemToAdd.height = video.height;

        this.page.poster.deleteItemById(activeItem.uid, false);
        this.page.activeSelection.clearSelection();
        void this.page.items.addItems.addVideoItem(item, itemToAdd, {zIndex});
      })
      .catch(() => {
        this.errorModalWhileReplacingItem();
      })
      .finally(() => {
        hideLoading('loadingVideo');
      });
  }

  public async replaceWithShape(itemToReplaceWith: PMWShapeData, activeItem: VectorItem | LineItem | RectangleItem): Promise<void> {
    if (!this.page.items.getItem(activeItem.uid)) {
      return;
    }

    const zIndex = activeItem.fabricObject.getZIndex();
    const left = activeItem.fabricObject.getX();
    const top = activeItem.fabricObject.getY();
    const {fill} = activeItem.toObject();

    try {
      const newItem = await this.page.items.addItems.getPMWShapeItem({...itemToReplaceWith, left, top});
      const scale = this.#getShapeReplacementScale(activeItem, newItem);
      const shapeObject: DeepPartial<BaseItemObject> = {
        angle: this.#getShapeReplacementAngle(activeItem, newItem),
        scaleX: scale,
        scaleY: scale,
        fill,
      };
      this.#copyShapeVisuals(shapeObject, activeItem, newItem);
      await newItem.updateFromObject(shapeObject, {undoable: false});

      this.page.poster.deleteItemById(activeItem.uid, false);
      this.page.activeSelection.clearSelection();
      await this.page.items.addItems.addItemToPage(newItem, {zIndex});
    } catch {
      this.errorModalWhileReplacingItem();
    }
  }

  /**
   * The replaced item's rotation wins, except when it was never rotated: then the new shape keeps the
   * angle baked into its own preset — the diamond preset is a square at 45°, and forcing it to 0 would
   * render an axis-aligned square, making the replace look like nothing happened.
   * Composing the two (old + preset) is not possible: the replaced item's preset angle cannot be
   * recovered from its current angle, and adding them would drift 45° → 90° → 135° across repeated
   * picks of the same shape.
   */
  #getShapeReplacementAngle(itemBeingReplaced: VectorItem | LineItem | RectangleItem, newItem: VectorItem | LineItem | RectangleItem): number {
    if (itemBeingReplaced.fabricObject.angle === 0) {
      return newItem.fabricObject.angle;
    }
    return itemBeingReplaced.fabricObject.angle;
  }

  /**
   * Shapes serialize no effects or masking, so the carried-over state is the common visual set plus the
   * border. A line's border has no editing UI and would paint an outline the user cannot remove, so it
   * is left at the new line's own default.
   */
  #copyShapeVisuals(itemToAdd: DeepPartial<BaseItemObject>, activeItem: VectorItem | LineItem | RectangleItem, newItem: VectorItem | LineItem | RectangleItem): void {
    this.#copyCommonVisuals(itemToAdd, activeItem);
    if (!newItem.isLine()) {
      itemToAdd.border = activeItem.border.toObject();
    }
  }

  /**
   * Fits the new shape into the replaced item's footprint while keeping its own aspect ratio.
   * When a line is on either side the fit uses width alone — a line's bounding box is only a few
   * px tall, and fitting into (or from) that height would collapse the replacement to a sliver.
   */
  #getShapeReplacementScale(itemBeingReplaced: VectorItem | LineItem | RectangleItem, newItem: VectorItem | LineItem | RectangleItem): number {
    const widthRatio = itemBeingReplaced.fabricObject.getScaledWidth() / newItem.fabricObject.width;
    if (itemBeingReplaced.isLine() || newItem.isLine()) {
      return widthRatio;
    }
    const heightRatio = itemBeingReplaced.fabricObject.getScaledHeight() / newItem.fabricObject.height;
    return Math.min(widthRatio, heightRatio);
  }

  public errorModalWhileReplacingItem(): void {
    getPosterUi().openErrorModal({
      message: window.i18next.t('pmwjs_general_ajax_error'),
      log: false,
    });
  }
}

/**
 * Resolves the replace target from the CURRENT selection rather than a uid bound at panel-open
 * time: each replace deletes the old item and selects its replacement, so a bound uid goes stale
 * after the first pick and would make every further pick a silent no-op. Selection-based targeting
 * is also how image replace supports picking repeatedly until the user is satisfied.
 */
const getItemBeingReplaced = (): Item | undefined => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (selectedItems?.length !== 1) {
    return undefined;
  }
  return selectedItems[0];
};

export const onReplaceShape = (itemToReplaceWith: ItemData): void => {
  if (itemToReplaceWith.type !== ElementDataType.PMW_SHAPE) {
    return;
  }

  const itemToReplace = getItemBeingReplaced();
  if (!itemToReplace || itemToReplace.isBackground) {
    return;
  }
  if (!(itemToReplace.isVector() || itemToReplace.isLine() || itemToReplace.isRectangle())) {
    return;
  }

  void itemToReplace.page.items.replaceItems.replaceWithShape(itemToReplaceWith, itemToReplace);
};

export const onReplaceMedia = (itemToReplaceWith: ItemData): void => {
  const itemToReplace = getItemBeingReplaced();
  if (!itemToReplace || !(itemToReplace.isImage() || itemToReplace.isVideo())) {
    return;
  }

  void itemToReplace.page.items.replaceItems.replaceMedia(itemToReplaceWith, itemToReplace);
};

export const onReplaceMediaSlide = (itemToReplaceWith: ItemData): void => {
  const itemToReplace = getItemBeingReplaced();
  if (!itemToReplace?.isSlideshow()) {
    return;
  }

  const activeSlide = itemToReplace.getSelectedSlide();
  if (!activeSlide.isMediaSlide()) {
    return;
  }

  void itemToReplace.page.items.replaceItems.replaceMediaSlide(itemToReplaceWith, activeSlide, itemToReplace);
};
